import React from 'react';
import {
  FaTasks, FaChevronDown, FaChevronUp, FaChevronRight,
  FaCheckSquare, FaRegSquare, FaComments, FaTimes, FaPlus,
  FaTrash, FaPaperclip, FaDownload, FaImage, FaPaperPlane,
} from 'react-icons/fa';
import { ChecklistItem, JobComment, JobAttachment } from '../services/PlantafelService';
import styles from './PlantafelSidePanel.module.css';

interface SidePanelChecklistProps {
  checklist: ChecklistItem[];
  checklistLoading: boolean;
  showChecklist: boolean;
  setShowChecklist: (show: boolean) => void;
  newChecklistItem: string;
  setNewChecklistItem: (text: string) => void;
  addingChecklistItem: boolean;
  togglingItemId: number | null;
  expandedSubtaskId: number | null;
  subtaskComments: JobComment[];
  subtaskAttachments: JobAttachment[];
  subtaskDataLoading: boolean;
  subtaskCommentText: string;
  setSubtaskCommentText: (text: string) => void;
  subtaskPendingFiles: File[];
  setSubtaskPendingFiles: React.Dispatch<React.SetStateAction<File[]>>;
  sendingSubtaskComment: boolean;
  subtaskFileInputRef: React.RefObject<HTMLInputElement>;
  handleToggleChecklistItem: (item: ChecklistItem) => void;
  handleAddChecklistItem: () => void;
  handleDeleteChecklistItem: (itemId: number) => void;
  handleToggleSubtaskExpand: (itemId: number) => void;
  handleSendSubtaskComment: () => void;
  handleDeleteSubtaskComment: (commentId: number) => void;
  handleDeleteSubtaskAttachment: (attachmentId: number) => void;
}

export function SidePanelChecklist({
  checklist, checklistLoading, showChecklist, setShowChecklist,
  newChecklistItem, setNewChecklistItem, addingChecklistItem,
  togglingItemId, expandedSubtaskId, subtaskComments, subtaskAttachments,
  subtaskDataLoading, subtaskCommentText, setSubtaskCommentText,
  subtaskPendingFiles, setSubtaskPendingFiles, sendingSubtaskComment,
  subtaskFileInputRef, handleToggleChecklistItem, handleAddChecklistItem,
  handleDeleteChecklistItem, handleToggleSubtaskExpand, handleSendSubtaskComment,
  handleDeleteSubtaskComment, handleDeleteSubtaskAttachment,
}: SidePanelChecklistProps): React.JSX.Element {
  const doneCount = checklist.filter(i => i.is_done).length;
  const progressPercent = checklist.length > 0 ? Math.round((doneCount / checklist.length) * 100) : 0;
  const allDone = doneCount === checklist.length && checklist.length > 0;

  return (
    <div style={{ marginBottom: '16px' }}>
      <button
        onClick={() => setShowChecklist(!showChecklist)}
        data-testid="sidepanel-checklist-toggle"
        className={styles.checklistToggle}
      >
        <span className={styles.checklistToggleLabel}>
          <FaTasks size={12} />
          Checkliste
          {checklist.length > 0 && (
            <span className={styles.checklistCount}>({doneCount}/{checklist.length})</span>
          )}
        </span>
        {showChecklist ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
      </button>

      {showChecklist && (
        <div data-testid="sidepanel-checklist-content" className={styles.checklistContent}>
          {checklistLoading ? (
            <div className={styles.checklistLoadingText}>Laden...</div>
          ) : (
            <>
              {checklist.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div className={styles.progressRow}>
                    <span className={styles.progressLabel}>Fortschritt</span>
                    <span className={styles.progressPercent}>{progressPercent}%</span>
                  </div>
                  <div className={styles.progressTrack}>
                    <div
                      data-testid="checklist-progress-bar"
                      className={styles.progressFill}
                      style={{
                        width: `${progressPercent}%`,
                        background: allDone ? '#10b981' : '#7c3aed',
                      }}
                    />
                  </div>
                </div>
              )}

              <div className={styles.checklistItemsCol}>
                {checklist.map((item) => {
                  const isExpanded = expandedSubtaskId === item.id;
                  const commentCount = item.comments_count || 0;

                  return (
                    <div
                      key={item.id}
                      data-testid={`checklist-item-${item.id}`}
                      style={{
                        border: isExpanded ? '1px solid #e0e3ef' : '1px solid transparent',
                        borderRadius: '6px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className={`${styles.checklistItemRow} ${item.is_done ? styles.checklistItemDone : isExpanded ? styles.checklistItemExpanded : ''}`}
                        onClick={() => handleToggleSubtaskExpand(item.id)}
                      >
                        <span className={`${styles.expandArrow} ${isExpanded ? styles.expandArrowExpanded : styles.expandArrowCollapsed}`}>
                          {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleChecklistItem(item); }}
                          disabled={togglingItemId === item.id}
                          className={`${styles.checkboxButton} ${togglingItemId === item.id ? styles.checkboxDisabled : styles.checkboxEnabled}`}
                          style={{ color: item.is_done ? '#10b981' : '#9ca3af' }}
                        >
                          {item.is_done ? <FaCheckSquare size={16} /> : <FaRegSquare size={16} />}
                        </button>
                        <span className={item.is_done ? styles.itemTextDone : styles.itemTextPending}>
                          {item.text || item.name || item.title || `Item #${item.id}`}
                        </span>
                        {commentCount > 0 && (
                          <span className={styles.commentCountBadge}>
                            <FaComments size={9} />{commentCount}
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteChecklistItem(item.id); }}
                          className={styles.deleteItemButton}
                          title="Löschen"
                        >
                          <FaTimes size={10} />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className={styles.expandedContent}>
                          {subtaskDataLoading ? (
                            <div className={styles.expandedLoadingText}>Laden...</div>
                          ) : (
                            <>
                              {renderSubtaskComments(item.id)}

                              {renderSubtaskStandaloneAttachments(item.id)}

                              {subtaskPendingFiles.length > 0 && (
                                <div className={styles.pendingFilesArea}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {subtaskPendingFiles.map((file, idx) => (
                                      <div key={idx} className={styles.pendingFileChip}>
                                        {file.type.startsWith('image/') ? <FaImage size={9} color="#666" /> : <FaPaperclip size={9} color="#666" />}
                                        <span className={styles.pendingFileName}>{file.name}</span>
                                        <button onClick={() => setSubtaskPendingFiles(prev => prev.filter((_, i) => i !== idx))} className={styles.pendingFileRemove}><FaTimes size={8} /></button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className={styles.subtaskInputRow}>
                                <input
                                  type="text"
                                  value={subtaskCommentText}
                                  onChange={(e) => setSubtaskCommentText(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' && (subtaskCommentText.trim() || subtaskPendingFiles.length > 0)) handleSendSubtaskComment(); }}
                                  placeholder="Kommentar..."
                                  className={styles.subtaskInput}
                                />
                                <button
                                  type="button"
                                  onClick={() => subtaskFileInputRef.current?.click()}
                                  className={styles.attachButton}
                                  title="Foto/Datei"
                                >
                                  <FaPaperclip size={11} color="#666" />
                                </button>
                                <button
                                  onClick={handleSendSubtaskComment}
                                  disabled={sendingSubtaskComment || (!subtaskCommentText.trim() && subtaskPendingFiles.length === 0)}
                                  className={styles.sendButton}
                                  style={{
                                    background: (sendingSubtaskComment || (!subtaskCommentText.trim() && subtaskPendingFiles.length === 0)) ? '#d1d5db' : '#021a4a',
                                    cursor: (sendingSubtaskComment || (!subtaskCommentText.trim() && subtaskPendingFiles.length === 0)) ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  <FaPaperPlane size={10} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className={styles.addItemRow}>
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                  placeholder="Neuer Eintrag..."
                  className={styles.addItemInput}
                />
                <button
                  onClick={handleAddChecklistItem}
                  disabled={!newChecklistItem.trim() || addingChecklistItem}
                  className={styles.addItemButton}
                  style={{
                    background: !newChecklistItem.trim() || addingChecklistItem ? '#e5e7eb' : '#7c3aed',
                    cursor: !newChecklistItem.trim() || addingChecklistItem ? 'not-allowed' : 'pointer',
                  }}
                >
                  <FaPlus size={10} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  function renderSubtaskComments(itemId: number) {
    const filteredComments = subtaskComments.filter(c => c.checklist_item_id === itemId);
    const filteredAttachments = subtaskAttachments.filter(a => a.checklist_item_id === itemId);

    if (filteredComments.length === 0) {
      return <div className={styles.noCommentsText} style={{ marginBottom: '10px' }}>Noch keine Kommentare</div>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
        {filteredComments.map((comment) => {
          const commentPhotos = filteredAttachments.filter(a => a.comment_id === comment.id && a.content_type?.startsWith('image/'));
          const commentFiles = filteredAttachments.filter(a => a.comment_id === comment.id && !a.content_type?.startsWith('image/'));
          return (
            <div key={comment.id} className={styles.commentBubble}>
              <div className={styles.commentHeader}>
                <span className={styles.commentAuthor}>{comment.author_name}</span>
                <div className={styles.commentMeta}>
                  <span className={styles.commentTime}>
                    {new Date(comment.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <button onClick={() => handleDeleteSubtaskComment(comment.id)} className={styles.commentDeleteBtn} title="Löschen">
                    <FaTrash size={9} />
                  </button>
                </div>
              </div>
              {comment.text !== '(Dateianhang)' && <p className={styles.commentText}>{comment.text}</p>}
              {commentPhotos.length > 0 && (
                <div style={{ marginTop: '6px' }} className={styles.photoGrid}>
                  {commentPhotos.map(photo => (
                    <div key={photo.id} className={styles.photoContainer}>
                      <a href={photo.file_url} target="_blank" rel="noopener noreferrer">
                        <img src={photo.thumbnail_url || photo.file_url} alt={photo.file_name} className={styles.photoCover} />
                      </a>
                      <button onClick={() => handleDeleteSubtaskAttachment(photo.id)} className={styles.photoDeleteBtn}><FaTrash size={6} /></button>
                    </div>
                  ))}
                </div>
              )}
              {commentFiles.length > 0 && (
                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {commentFiles.map(file => (
                    <div key={file.id} className={styles.fileRow}>
                      <div className={styles.fileInfo}>
                        <FaPaperclip size={8} style={{ color: '#666' }} />
                        <span className={styles.fileName}>{file.file_name}</span>
                      </div>
                      <div className={styles.fileActions}>
                        <a href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#021a4a' }}><FaDownload size={8} /></a>
                        <button onClick={() => handleDeleteSubtaskAttachment(file.id)} className={styles.fileDeleteBtn}><FaTrash size={8} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderSubtaskStandaloneAttachments(itemId: number) {
    const thisSubtaskAttachments = subtaskAttachments.filter(a => a.checklist_item_id === itemId);
    const standalonePhotos = thisSubtaskAttachments.filter(a => !a.comment_id && a.content_type?.startsWith('image/'));
    const standaloneFiles = thisSubtaskAttachments.filter(a => !a.comment_id && !a.content_type?.startsWith('image/'));
    if (standalonePhotos.length === 0 && standaloneFiles.length === 0) return null;

    return (
      <div className={styles.standaloneFilesSection}>
        <div className={styles.standaloneFilesHeader}>
          <FaPaperclip size={10} style={{ color: '#666' }} />
          <span className={styles.standaloneFilesLabel}>Dateien</span>
        </div>
        {standalonePhotos.length > 0 && (
          <div className={styles.photoGrid} style={{ marginBottom: standaloneFiles.length > 0 ? '6px' : '0' }}>
            {standalonePhotos.map(photo => (
              <div key={photo.id} className={styles.photoContainer}>
                <a href={photo.file_url} target="_blank" rel="noopener noreferrer">
                  <img src={photo.thumbnail_url || photo.file_url} alt={photo.file_name} className={styles.photoCover} />
                </a>
                <button onClick={() => handleDeleteSubtaskAttachment(photo.id)} className={styles.photoDeleteBtn}><FaTrash size={6} /></button>
              </div>
            ))}
          </div>
        )}
        {standaloneFiles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {standaloneFiles.map(file => (
              <div key={file.id} className={styles.fileRow}>
                <div className={styles.fileInfo}>
                  <FaPaperclip size={8} style={{ color: '#666' }} />
                  <span className={styles.fileName}>{file.file_name}</span>
                </div>
                <div className={styles.fileActions}>
                  <a href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#021a4a' }}><FaDownload size={8} /></a>
                  <button onClick={() => handleDeleteSubtaskAttachment(file.id)} className={styles.fileDeleteBtn}><FaTrash size={8} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
